import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, getCurrentSession } from '../lib/supabaseClient';

export const DISPLAY_MODES = { STANDARD: 'standard', TACTICAL: 'tactical' };

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayMode, setDisplayMode] = useState(() => 
    localStorage.getItem('plateprofit_display_mode') || DISPLAY_MODES.STANDARD
  );

  useEffect(() => {
    // Initialize session once
    getCurrentSession().then(({ session }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes - debounced to prevent race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Ignore intermediate events during signup/verification
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    document.body.classList.remove('display-standard', 'display-tactical');
    document.body.classList.add(`display-${displayMode}`);
    localStorage.setItem('plateprofit_display_mode', displayMode);
  }, [displayMode]);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, options = {}) => {
    try {
      // Let Supabase handle error parsing - do NOT manually read response
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: options,
      });
      
      // Return the result as-is - Supabase has already parsed any errors
      return { data, error };
    } catch (err) {
      // If Supabase throws, wrap it
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const toggleDisplayMode = () => {
    setDisplayMode(prev => 
      prev === DISPLAY_MODES.STANDARD ? DISPLAY_MODES.TACTICAL : DISPLAY_MODES.STANDARD
    );
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      displayMode, 
      signIn, 
      signUp, 
      signOut, 
      toggleDisplayMode,
      supabase // Expose supabase client for OTP verification
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
